import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { PageHero } from "@/components/PageHero";
import { usePageMeta } from "@/hooks/usePageMeta";
import { SITE } from "@/data/site";

const SECTIONS = [
  {
    h: "1. About these terms",
    body: `These Terms & Conditions ("Terms") govern your use of usddacademy.co.uk and any services, classes, workshops, events or registrations booked through it. By using the site or booking a service you agree to these Terms.`,
  },
  {
    h: "2. The service",
    body: `USDD Academy provides street-dance and creative-performance training, workshops, kids classes, online courses, events and bespoke bookings for schools and organisations across the UK.`,
  },
  {
    h: "3. Bookings & payment",
    body: `Bookings are made via our website, Acuity Scheduling or directly with our team. Payment is taken via Stripe or PayPal (where enabled). Drop-in fees, monthly memberships, term fees and package prices are listed on the relevant page and may change with notice. Once a booking is paid for, you'll receive an email confirmation.`,
  },
  {
    h: "4. Cancellations & refunds",
    body: `• Drop-in classes: cancel up to 12 hours before the class for a full credit. No-shows and late cancellations are non-refundable.\n• Term & block bookings: cancel within 7 days of purchase for a full refund (provided no sessions have been attended). After 7 days, partial refunds are at our discretion.\n• Workshops & events: tickets are non-refundable but may be transferred to another attendee with our written agreement.\n• Bespoke bookings (schools, organisations, festivals): cancellation terms are set in the individual proposal.\nWhere required by UK consumer law (Consumer Contracts Regulations 2013) your statutory rights to cancel within 14 days remain unaffected, subject to limitations for services that have already begun.`,
  },
  {
    h: "5. Attendance & safety",
    body: `Classes are physical. You confirm you're medically fit to participate and will inform us of any condition we should know about. USDD Academy is not liable for personal injury arising from your participation, except where caused by our negligence. Children must be registered by a parent/guardian and dropped off / collected as per studio guidelines.`,
  },
  {
    h: "6. Student conduct",
    body: `We reserve the right to refuse entry or remove a student (without refund) where behaviour is unsafe, abusive, discriminatory or otherwise damages the experience of other students or staff.`,
  },
  {
    h: "7. Media & photography",
    body: `Classes, showcases and events may be photographed or filmed for promotional use. Where you do not consent to being filmed, please notify the lead instructor before the session. Parents/guardians have a right to opt out on behalf of registered children.`,
  },
  {
    h: "8. Intellectual property",
    body: `All choreography, course material, branding, photography, videos and written content on this site are the property of USDD Academy or its licensors. You may not copy, redistribute or commercially use the content without written permission.`,
  },
  {
    h: "9. Online courses",
    body: `Online courses are licensed for personal use only. Sharing login credentials, downloading or redistributing content is a breach of these Terms and may result in immediate access removal without refund.`,
  },
  {
    h: "10. Third-party services",
    body: `Bookings may use Acuity Scheduling, payments Stripe / PayPal, and forms Jotform. Your interaction with these providers is also governed by their own terms and privacy policies.`,
  },
  {
    h: "11. Liability",
    body: `Nothing in these Terms limits our liability for death, personal injury caused by negligence, or fraud. Otherwise our total liability arising out of any single booking is limited to the amount you paid for that booking.`,
  },
  {
    h: "12. Governing law",
    body: `These Terms are governed by the laws of England & Wales. Any disputes will be resolved in the courts of England & Wales.`,
  },
  {
    h: "13. Contact",
    body: `Questions about these Terms? Email info@usddnetworks.co.uk or call 07513 192613.`,
  },
];

export default function TermsPage() {
  usePageMeta(
    "Terms & Conditions · USDD Academy",
    "Terms & Conditions covering bookings, payments, cancellations, online courses and use of the USDD Academy website."
  );
  return (
    <>
      <Header />
      <main>
        <PageHero
          overline="Legal"
          title="Terms &"
          highlight="Conditions."
          description="The terms that apply when you book a class, register a child, attend an event or use the USDD Academy website."
          bg={SITE.classImg4}
        />
        <section className="py-16 sm:py-20 bg-black">
          <div className="max-w-3xl mx-auto px-6 sm:px-8">
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-8">
              Effective date · February 2026
            </div>
            {SECTIONS.map((s) => (
              <div key={s.h} data-testid={`terms-section-${s.h.split(".")[0]}`} className="mb-10">
                <h2 className="font-display text-2xl sm:text-3xl text-white tracking-wide mb-3">{s.h}</h2>
                <p className="text-sm sm:text-base text-zinc-300 leading-relaxed whitespace-pre-line">{s.body}</p>
              </div>
            ))}
            <div className="mt-12 pt-8 border-t border-white/10 text-xs text-zinc-500">
              We may update these Terms occasionally. The most current version always lives on this page.
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
